import { useTranslation } from 'react-i18next';
import { Plus, Trophy } from 'lucide-react';
import { StatusButton } from '../ui/status-button';

interface BoulderActionButtonsProps {
  topped: boolean;
  onRecordAttempt: () => void | Promise<void>;
  onRecordZone: () => void | Promise<void>;
  onRecordTop: () => void | Promise<void>;
  /**
   * Nothing more goes on this card, for a reason that is not the top: a round
   * closed from the panel takes no marks from anybody. Shown rather than hidden
   * so the card still reads as a card.
   */
  disabled?: boolean;
  className?: string;
}

/**
 * The three things that can be added to a boulder, wherever it is being scored
 * — the athlete's own row or the judge's table. One tap each: there is no
 * confirmation step, and a wrong tap is taken back from the edit dialog.
 * Once the boulder is topped there is nothing left to add and all three go
 * disabled.
 *
 * Each one carries its own answer: it spins while the attempt is being saved
 * and shows a check when it lands, so a judge working down the table sees the
 * result on the button they pressed. That only holds if the handler rejects
 * when the save fails — a handler that swallows its error reads as a success
 * here.
 */
export function BoulderActionButtons({
  topped,
  onRecordAttempt,
  onRecordZone,
  onRecordTop,
  disabled = false,
  className = '',
}: BoulderActionButtonsProps) {
  const { t } = useTranslation();
  const off = topped || disabled;

  return (
    <div className={`flex gap-2 ${className}`}>
      <StatusButton
        size="sm"
        variant="outline"
        className="flex-1"
        onClick={onRecordAttempt}
        disabled={off}
        data-testid="attempt-action-button"
      >
        <Plus className="h-4 w-4 mr-1" />
        {t('judgeBoulder.attempt', { defaultValue: 'Attempt' })}
      </StatusButton>
      <StatusButton
        size="sm"
        variant="outline"
        className="flex-1 text-yellow-600 border-yellow-600 hover:bg-yellow-50"
        onClick={onRecordZone}
        disabled={off}
        data-testid="zone-action-button"
      >
        <Plus className="h-4 w-4 mr-1" />
        {t('judgeBoulder.zone', { defaultValue: 'Zone' })}
      </StatusButton>
      <StatusButton
        size="sm"
        variant="outline"
        className="flex-1 text-green-600 border-green-600 hover:bg-green-50"
        onClick={onRecordTop}
        disabled={off}
        data-testid="top-action-button"
      >
        <Trophy className="h-4 w-4 mr-1" />
        {t('judgeBoulder.top', { defaultValue: 'Top' })}
      </StatusButton>
    </div>
  );
}
